/** @jsxImportSource @emotion/react */
import React from "react";
import {css} from "@emotion/react";

import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";

const variantColors = (theme, variant) => {
  const d = theme.palette.dashboards;
  if (variant === "good") return {fg: d.ahead, bg: d.aheadBackground, text: d.aheadText};
  if (variant === "bad") return {fg: d.behind, bg: d.behindBackground, text: d.behindText};
  return {fg: theme.palette.text.secondary, bg: theme.palette.background.chip, text: theme.palette.text.secondary};
};

// bhi-collapse points down; rotate 180 for the up arrow.
// Direction comes from the change itself and colour from the variant: with "higher is
// bad" a rise is still a rise, so it reads as red but pointing up.
const ChangeBadge = ({changePct, changeLabel, variant}) => {
  const {theme} = React.useContext(CustomThemeContext);
  if (!changeLabel) return null;
  const {bg, fg, text} = variantColors(theme, variant);
  // Arrow conveys direction; omit the minus sign to avoid redundancy with the down arrow.
  const sign = changePct > 0 ? "+" : "";

  return (
    <span data-test-id="impact-change-badge" css={css`
      display: inline-flex;
      align-items: center;
      gap: 3px;
      background: ${bg};
      color: ${text};
      border-radius: 4px;
      padding: 1px 6px;
      font-size: 11px;
      font-weight: 500;
      white-space: nowrap;
    `}>
      {!!changePct && (
        <i
          className="bhi-collapse"
          css={css`
            font-size: 9px;
            color: ${fg};
            line-height: 1;
            transform: ${changePct > 0 ? "rotate(180deg)" : "none"};
          `} />
      )}
      {sign}{changeLabel}%
    </span>
  );
};

const Row = ({qualifier, label, value, onClick, indented, emphasis}) => {
  const {theme} = React.useContext(CustomThemeContext);
  return (
    <div
      data-test-id="impact-detail"
      onClick={onClick}
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 5px 12px 5px ${indented ? 28 : 12}px;
        font-size: ${emphasis ? 13 : 12}px;
        font-weight: ${emphasis ? 500 : 400};
        color: ${emphasis ? theme.palette.text.main : theme.palette.text.secondary};
        border-top: 1px solid ${theme.palette.dashboards.divider};
        cursor: ${onClick ? "pointer" : "default"};
        &:hover {
          background: ${onClick ? theme.palette.action.hover : "transparent"};
        }
      `}>
      <span css={css`min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`}>
        {label}
        {qualifier && (
          <span css={css`color: ${theme.palette.text.secondary}; font-weight: 400; margin-left: 6px;`}>
            {qualifier}
          </span>
        )}
      </span>
      <span css={css`white-space: nowrap; font-variant-numeric: tabular-nums;`}>
        {value}
      </span>
    </div>
  );
};

// Every section of the panel in one dialog, each as its own card. Scrolls rather than
// truncating, since a panel can hold more sections than a dialog can show at once.
const ImpactDetails = ({groups}) => {
  const {theme} = React.useContext(CustomThemeContext);

  return (
    <div data-test-id="impact-details" css={css`
      padding: 12px;
      /* the dialog's own content wrapper already insets by 15px, which is too much */
      margin: 0 -15px;
      background: ${theme.palette.background.card};
      max-height: 70vh;
      overflow-y: auto;
    `}>
      {groups.map((group, groupIndex) => (
        <div
          key={groupIndex}
          data-test-id="impact-detail-group"
          css={css`
            border: 1px solid ${theme.palette.divider};
            border-radius: 6px;
            overflow: hidden;
            margin-bottom: 12px;
            &:last-of-type { margin-bottom: 0; }
          `}>
          <div css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1rem;
            padding: 6px 12px;
            background: ${theme.palette.background.paper};
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.3px;
            text-transform: uppercase;
            color: ${theme.palette.text.secondary};
          `}>
            <span css={css`min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`}>
              {group.title}
            </span>
            <ChangeBadge
              changePct={group.changePct}
              changeLabel={group.changeLabel}
              variant={group.variant} />
          </div>
          {group.rows.map((row, rowIndex) => (
            <Row
              key={rowIndex}
              qualifier={row.qualifier}
              label={row.label}
              value={row.value}
              indented={row.indented}
              emphasis={!!row.onClick}
              onClick={row.onClick} />
          ))}
        </div>
      ))}
    </div>
  );
};

export default ImpactDetails;
